import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, Mail, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import { TwoFactorQr } from '../components/TwoFactorQr'
import { PasswordField } from '../components/PasswordField'
import { useAuth } from '../context/AuthContext'
import { ROLE_LABELS, type AccountProof, type AccountSecurity, type TwoFactorSetup } from '../types/identity'

const INPUT =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base sm:text-sm dark:border-white/10 dark:bg-white/5'
const BUTTON =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white hover:bg-salsa-600 disabled:opacity-60'
const CARD = 'card space-y-3 p-4'
const SUBTLE =
  'inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5'
const BANNER = 'flex items-start gap-2 rounded-2xl border px-4 py-3 text-xs'
const ERROR_TONE =
  'border-salsa-200 bg-salsa-50/70 text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200'
const OK_TONE =
  'border-emerald-200 bg-emerald-50/70 text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200'

/** Password, or a code we e-mail — one **independent** picker per card (D-36). */
function ProofPicker({
  onChange,
  label,
}: {
  onChange: (proof: AccountProof) => void
  label: string
}) {
  const [useCode, setUseCode] = useState(false)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  function push(next: { password?: string; code?: string; useCode?: boolean }) {
    const mode = next.useCode ?? useCode
    const pw = next.password ?? password
    const cd = next.code ?? code
    onChange(mode ? { verification_code: cd } : { current_password: pw })
  }

  async function sendCode() {
    setSending(true)
    setProblem(null)
    try {
      await api.sendAccountCode()
      setSent(true)
      setUseCode(true)
      push({ useCode: true })
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : 'Could not send the code')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {label}
      </p>
      <div className="flex flex-wrap items-center gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5">
        {(['password', 'code'] as const).map((option) => {
          const active = option === 'code' ? useCode : !useCode
          return (
            <button
              key={option}
              type="button"
              onClick={() => {
                const nextUseCode = option === 'code'
                setUseCode(nextUseCode)
                push({ useCode: nextUseCode })
              }}
              className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? 'bg-white text-slate-900 shadow-sm dark:bg-white/10 dark:text-white'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
              }`}
            >
              {option === 'password' ? 'My password' : 'E-mail me a code'}
            </button>
          )
        })}
      </div>

      {useCode ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
              push({ code: event.target.value })
            }}
            inputMode="numeric"
            placeholder="6-digit code"
            className={`${INPUT} flex-1 font-mono tracking-[0.2em]`}
          />
          <button type="button" onClick={() => void sendCode()} disabled={sending} className={SUBTLE}>
            {sending ? 'Sending…' : sent ? 'Send a new code' : 'Send me a code'}
          </button>
        </div>
      ) : (
        <PasswordField
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
            push({ password: event.target.value })
          }}
          placeholder="Current password"
          autoComplete="current-password"
          revealLabel="current password"
          inputClassName={INPUT}
        />
      )}

      {problem && <p className="text-[11px] text-salsa-600 dark:text-salsa-400">{problem}</p>}
    </div>
  )
}

/** Self-service account settings (§13.8 · D-35/D-36) — your own details only. */
export function AccountSettingsPage() {
  const { user, refresh } = useAuth()
  const [security, setSecurity] = useState<AccountSecurity | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // One independent proof per card — they must never share state (fixed 2026-10-16).
  const [passwordProof, setPasswordProof] = useState<AccountProof>({})
  const [emailProof, setEmailProof] = useState<AccountProof>({})
  const [twoFactorProof, setTwoFactorProof] = useState<AccountProof>({})
  const [newPassword, setNewPassword] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [enrolment, setEnrolment] = useState<TwoFactorSetup | null>(null)
  const [enrolCode, setEnrolCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])

  const load = useCallback(async () => {
    try {
      setSecurity(await api.fetchAccountSecurity())
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your account')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function run(kind: string, action: () => Promise<string | null>) {
    setBusy(kind)
    setError(null)
    setNotice(null)
    try {
      const message = await action()
      if (message) setNotice(message)
      await load()
      void refresh()
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'That did not work — nothing was changed',
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className={`${BANNER} ${ERROR_TONE}`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className={`${BANNER} ${OK_TONE}`}>
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{notice}</span>
        </div>
      )}

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Your own account — {security?.email ?? user?.email} ·{' '}
        {ROLE_LABELS[security?.role ?? user?.role ?? 'team_member']}
      </p>

      <form
        className={CARD}
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          void run('password', async () => {
            const result = await api.changeOwnPassword({ ...passwordProof, new_password: newPassword })
            setNewPassword('')
            return result.detail ?? 'Password changed.'
          })
        }}
      >
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          <ShieldCheck className="h-4 w-4 text-salsa-500" aria-hidden="true" /> Password
        </h2>
        <ProofPicker label="Confirm with" onChange={setPasswordProof} />
        <PasswordField
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          placeholder="New password (12+ characters)"
          autoComplete="new-password"
          revealLabel="new password"
          inputClassName={INPUT}
          required
        />
        <button type="submit" disabled={busy === 'password'} className={BUTTON}>
          {busy === 'password' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Update password
        </button>
        <p className="text-[11px] text-slate-400 dark:text-slate-500">
          Every other device is signed out afterwards.
        </p>
      </form>

      <form
        className={CARD}
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          void run('email', async () => {
            const result = await api.changeOwnEmail({ ...emailProof, new_email: newEmail })
            setNewEmail('')
            return result.detail ?? 'Sign-in address updated.'
          })
        }}
      >
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          <Mail className="h-4 w-4 text-salsa-500" aria-hidden="true" /> Sign-in e-mail address
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {security?.email}
          {security?.email_verified ? ' · verified' : ' · not verified yet'}
        </p>
        <ProofPicker label="Confirm with" onChange={setEmailProof} />
        <input
          type="email"
          value={newEmail}
          onChange={(event) => setNewEmail(event.target.value)}
          placeholder="new@email.com"
          className={INPUT}
          required
        />
        <button type="submit" disabled={busy === 'email'} className={BUTTON}>
          {busy === 'email' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Change address
        </button>
        <p className="text-[11px] text-slate-400 dark:text-slate-500">
          Other devices are signed out, and the new address must be confirmed from the link we e-mail.
        </p>
      </form>

      <section className={CARD}>
        <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          <Smartphone className="h-4 w-4 text-salsa-500" aria-hidden="true" /> Two-factor
          authentication
          <span
            className={`badge ${security?.two_factor_enabled ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-slate-500/15 text-slate-600 dark:text-slate-300'}`}
          >
            {security?.two_factor_enabled ? 'on' : 'off'}
          </span>
        </h2>

        {security?.required_for_role && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {ROLE_LABELS[security.role]} must keep two-factor switched on — you can replace the
            authenticator, but not remove it.
          </p>
        )}

        {recoveryCodes.length > 0 && (
          <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 dark:border-amber-500/25 dark:bg-amber-500/10">
            <p className="text-xs text-amber-900 dark:text-amber-200">
              New recovery codes — each works once, store them somewhere safe.
            </p>
            <ul className="grid grid-cols-2 gap-1 font-mono text-xs">
              {recoveryCodes.map((item) => (
                <li key={item} className="rounded-lg bg-white px-2 py-1 text-center dark:bg-black/30">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {enrolment ? (
          <form
            className="space-y-3"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              void run('2fa', async () => {
                const result = await api.confirmTwoFactor(enrolCode)
                setEnrolment(null)
                setEnrolCode('')
                if (result.recovery_codes.length > 0) {
                  setRecoveryCodes(result.recovery_codes)
                  return 'Two-factor is on. Save the recovery codes below.'
                }
                return 'Authenticator replaced.'
              })
            }}
          >
            <TwoFactorQr
              otpauthUri={enrolment.otpauth_uri}
              secret={enrolment.secret}
              account={enrolment.account}
            />
            <input
              value={enrolCode}
              onChange={(event) => setEnrolCode(event.target.value)}
              inputMode="numeric"
              placeholder="000000"
              className={`${INPUT} text-center font-mono text-lg tracking-[0.3em]`}
              required
            />
            <button type="submit" disabled={busy === '2fa'} className={BUTTON}>
              {busy === '2fa' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Activate two-factor
            </button>
          </form>
        ) : (
          <>
            <ProofPicker label="Confirm with" onChange={setTwoFactorProof} />
            {security?.two_factor_enabled && security.can_remove_2fa && (
              <button
                type="button"
                className={SUBTLE}
                disabled={busy === '2fa'}
                onClick={() =>
                  void run('2fa', async () => {
                    const result = await api.disableOwnTwoFactor(twoFactorProof)
                    setRecoveryCodes([])
                    return result.detail ?? 'Two-factor switched off.'
                  })
                }
              >
                <ShieldOff className="h-3 w-3" aria-hidden="true" /> Switch off
              </button>
            )}
            <div>
              <button
                type="button"
                className={SUBTLE}
                disabled={busy === '2fa'}
                onClick={() =>
                  void run('2fa', async () => {
                    const setup = security?.two_factor_enabled
                      ? await api.rotateOwnTwoFactor(twoFactorProof)
                      : await api.startTwoFactorSetup()
                    setEnrolment(setup)
                    setRecoveryCodes([])
                    return security?.two_factor_enabled
                      ? 'Scan the new code to replace your authenticator.'
                      : null
                  })
                }
              >
                {security?.two_factor_enabled ? 'Replace authenticator' : 'Turn on two-factor'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
